import { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  EventInvitee,
  EventParticipant,
  EventSummary,
} from "src/app/models/screens/events.model";
import {
  File as CloudieFile,
  Folder,
} from "src/app/models/screens/files.model";
import {
  cancelEvent,
  getCloudieItems,
  getCloudieMediaUrl,
  getEventDetail,
  getParentChildren,
  rescheduleEvent,
  searchEventInvitees,
  sendEventInvitation,
  setEventParticipantMediaAccess,
  uploadCloudieFile,
  withdrawFromEvent,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

export interface EventMediaUploadTask {
  id: string;
  fileName: string;
  progress: number;
  done: boolean;
  error?: string;
}

const EventDetailScreenViewModel = () => {
  const { eventId } = useParams<{ eventId: string }>();

  const [isSnackbarError, setIsSnackbarError] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] = useState(false);

  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");

  const [event, setEvent] = useState<EventSummary | null>(null);
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [viewerIsCreator, setViewerIsCreator] = useState(false);
  const [viewerIsParticipant, setViewerIsParticipant] = useState(false);
  const [eventHasFinished, setEventHasFinished] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteSearchText, setInviteSearchText] = useState("");
  const [inviteSearchResults, setInviteSearchResults] = useState<
    EventInvitee[]
  >([]);
  const [isSearchingInvitees, setIsSearchingInvitees] = useState(false);
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);

  const [isMediaAccessDialogOpen, setIsMediaAccessDialogOpen] = useState(false);
  const [updatingMediaUserId, setUpdatingMediaUserId] = useState<string | null>(
    null,
  );

  const [eventMedia, setEventMedia] = useState<CloudieFile[]>([]);
  const [eventMediaFolders, setEventMediaFolders] = useState<Folder[]>([]);
  const [isLoadingEventMedia, setIsLoadingEventMedia] = useState(false);
  const [uploadTasks, setUploadTasks] = useState<EventMediaUploadTask[]>([]);
  const uploadCounterRef = useRef(0);

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Dayjs | null>(null);
  const [rescheduleStartTime, setRescheduleStartTime] = useState<Dayjs | null>(
    null,
  );
  const [rescheduleDurationMinutes, setRescheduleDurationMinutes] =
    useState<string>("");
  const [isRescheduling, setIsRescheduling] = useState(false);

  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState<string>("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const showError = (msg: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(msg);
    setIsDisplayingSnackbar(true);
  };
  const showSuccess = (msg: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(msg);
    setIsDisplayingSnackbar(true);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const formatCurrency = (amount: number, currency: string): string => {
    if (currency === "GBP") return `£${amount.toFixed(2)}`;
    return `${amount.toFixed(2)} ${currency}`;
  };

  const mediaUrl = (file: CloudieFile, thumbnail: boolean): string =>
    getCloudieMediaUrl(file.id, thumbnail, getCurrentChildId());

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadDetail = useCallback(async () => {
    if (!eventId) return;
    setIsLoadingDetail(true);
    const response = await getEventDetail(eventId, getCurrentChildId());
    setIsLoadingDetail(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setEvent(response.event ?? null);
    setParticipants(response.participants ?? []);
    setViewerIsCreator(!!response.viewerIsCreator);
    setViewerIsParticipant(!!response.viewerIsParticipant);
    setEventHasFinished(!!response.eventHasFinished);
  }, [eventId, getCurrentChildId]);

  const loadEventMedia = useCallback(
    async (folderId: string | null) => {
      if (!folderId) {
        setEventMedia([]);
        setEventMediaFolders([]);
        return;
      }
      setIsLoadingEventMedia(true);
      const response = await getCloudieItems(folderId, getCurrentChildId());
      setIsLoadingEventMedia(false);
      if (!response.success) {
        setEventMedia([]);
        setEventMediaFolders([]);
        return;
      }
      setEventMedia(response.files ?? []);
      setEventMediaFolders(response.folders ?? []);
    },
    [getCurrentChildId],
  );

  useEffect(() => {
    const role = localStorage.getItem(localStorageConstants.ROLE);
    if (role) setUserRole(role);
  }, []);

  useEffect(() => {
    if (userRole === "Parent") loadParentChildren();
  }, [loadParentChildren, userRole]);

  useEffect(() => {
    if (!userRole || !eventId) return;
    loadDetail();
  }, [userRole, eventId, selectedOwnerId, loadDetail]);

  useEffect(() => {
    if (!event) return;
    if (!event.cloudieFolderId) return;
    if (!viewerIsCreator && !viewerIsParticipant) return;
    loadEventMedia(event.cloudieFolderId);
  }, [
    event?.id,
    event?.cloudieFolderId,
    viewerIsCreator,
    viewerIsParticipant,
    event,
    loadEventMedia,
  ]);

  useEffect(() => {
    if (!isInviteDialogOpen) return;
    if (!eventId) return;
    const t = setTimeout(async () => {
      setIsSearchingInvitees(true);
      const res = await searchEventInvitees(
        eventId,
        inviteSearchText.trim(),
        getCurrentChildId(),
      );
      setIsSearchingInvitees(false);
      if (res.success) setInviteSearchResults(res.users ?? []);
    }, 300);
    return () => clearTimeout(t);
  }, [inviteSearchText, isInviteDialogOpen, eventId, getCurrentChildId]);

  const onOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
  };

  const openInviteDialog = () => {
    setIsInviteDialogOpen(true);
    setInviteSearchText("");
    setInviteSearchResults([]);
  };
  const closeInviteDialog = () => {
    setIsInviteDialogOpen(false);
    setInviteSearchText("");
    setInviteSearchResults([]);
  };

  const onInviteUser = async (user: EventInvitee) => {
    if (!eventId) return;
    setInvitingUserId(user.id);
    const response = await sendEventInvitation(
      eventId,
      user.id,
      getCurrentChildId(),
    );
    setInvitingUserId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    showSuccess(`Invitation sent to ${user.firstName} ${user.lastName}.`);
    setInviteSearchResults((prev) => prev.filter((u) => u.id !== user.id));
  };

  const openMediaAccessDialog = () => setIsMediaAccessDialogOpen(true);
  const closeMediaAccessDialog = () => setIsMediaAccessDialogOpen(false);

  const onToggleParticipantMediaAccess = async (
    participantId: string,
    allow: boolean,
  ) => {
    if (!eventId) return;
    setUpdatingMediaUserId(participantId);
    const response = await setEventParticipantMediaAccess(
      eventId,
      participantId,
      allow,
      getCurrentChildId(),
    );
    setUpdatingMediaUserId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setParticipants((prev) =>
      prev.map((p) =>
        p.id === participantId ? { ...p, excludedFromMedia: !allow } : p,
      ),
    );
    setEvent((prev) =>
      prev
        ? { ...prev, excludedMediaUserIds: response.excludedMediaUserIds ?? [] }
        : prev,
    );
    showSuccess(allow ? "Access granted." : "Access revoked.");
    if (event?.cloudieFolderId) loadEventMedia(event.cloudieFolderId);
  };

  const dismissUploadTask = (taskId: string) => {
    setUploadTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  const onMediaFilesSelected = async (files: FileList | null) => {
    if (!files) return;
    if (!event?.cloudieFolderId) return;
    const folderId = event.cloudieFolderId;
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        showError(`${file.name} is not a photo or video and was skipped.`);
        return;
      }
      const taskId = `event-upload-${++uploadCounterRef.current}`;
      setUploadTasks((prev) => [
        ...prev,
        { id: taskId, fileName: file.name, progress: 0, done: false },
      ]);
      uploadCloudieFile(file, folderId, getCurrentChildId(), (pct: number) => {
        setUploadTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, progress: pct } : t)),
        );
      }).then((res: any) => {
        if (!res?.success) {
          setUploadTasks((prev) =>
            prev.map((t) =>
              t.id === taskId
                ? {
                    ...t,
                    error: res?.error ?? "Upload failed.",
                    done: true,
                  }
                : t,
            ),
          );
          showError(`${file.name}: ${res?.error ?? "Upload failed."}`);
          return;
        }
        setUploadTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? { ...t, progress: 100, done: true } : t,
          ),
        );
        loadEventMedia(folderId);
      });
    });
  };

  const eventHasStarted = useMemo(() => {
    if (!event) return false;
    const todayStr = new Date().toISOString().split("T")[0];
    if (event.date < todayStr) return true;
    if (event.date > todayStr) return false;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return nowMinutes >= event.from;
  }, [event]);

  const participantGroups = useMemo(() => {
    type Group = {
      id: string;
      anchor: EventParticipant;
      others: EventParticipant[];
    };
    const groupMap = new Map<string, EventParticipant[]>();
    for (const p of participants) {
      const key = p.companionGroupId ?? `solo-${p.id}`;
      const list = groupMap.get(key);
      if (list) list.push(p);
      else groupMap.set(key, [p]);
    }
    const groups: Group[] = [];
    groupMap.forEach((members, groupId) => {
      const anchor = members.find((m) => m.role === "Model") ?? members[0];
      const others = members.filter((m) => m.id !== anchor.id);
      groups.push({ id: groupId, anchor, others });
    });
    groups.sort((a, b) => {
      const creatorId = event?.creatorId;
      const aHasCreator =
        creatorId !== undefined &&
        (a.anchor.id === creatorId || a.others.some((o) => o.id === creatorId));
      const bHasCreator =
        creatorId !== undefined &&
        (b.anchor.id === creatorId || b.others.some((o) => o.id === creatorId));
      if (aHasCreator && !bHasCreator) return -1;
      if (!aHasCreator && bHasCreator) return 1;
      return a.anchor.fullName.localeCompare(b.anchor.fullName);
    });
    return groups;
  }, [participants, event?.creatorId]);

  const canCancel = !!(
    event &&
    viewerIsCreator &&
    !event.isCancelled &&
    !eventHasStarted
  );
  const canReschedule = canCancel;
  const canWithdraw = !!(
    event &&
    viewerIsParticipant &&
    !viewerIsCreator &&
    !event.isCancelled &&
    !eventHasStarted
  );

  const openCancelDialog = () => setIsCancelDialogOpen(true);
  const closeCancelDialog = () => setIsCancelDialogOpen(false);

  const onConfirmCancel = async () => {
    if (!event) return;
    setIsCancelling(true);
    const response = await cancelEvent(event.id, getCurrentChildId());
    setIsCancelling(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setEvent(response.event ?? null);
    setIsCancelDialogOpen(false);
    showSuccess("Event cancelled.");
  };

  const openRescheduleDialog = () => {
    if (!event) return;
    setRescheduleDate(null);
    setRescheduleStartTime(null);
    setRescheduleDurationMinutes(String(event.durationMinutes));
    setIsRescheduleDialogOpen(true);
  };
  const closeRescheduleDialog = () => setIsRescheduleDialogOpen(false);

  const onConfirmReschedule = async () => {
    if (!event) return;
    if (!rescheduleDate) {
      showError("Please pick a new date.");
      return;
    }
    if (!rescheduleStartTime || !rescheduleStartTime.isValid()) {
      showError("Please pick a valid start time.");
      return;
    }
    const fromNum =
      rescheduleStartTime.hour() * 60 + rescheduleStartTime.minute();
    const durationNum = parseInt(rescheduleDurationMinutes, 10);
    if (Number.isNaN(durationNum) || durationNum <= 0) {
      showError("Please enter a valid duration in minutes.");
      return;
    }
    if (fromNum + durationNum > 24 * 60) {
      showError("Event end time must fall within the same day.");
      return;
    }
    setIsRescheduling(true);
    const response = await rescheduleEvent(
      event.id,
      rescheduleDate.format("YYYY-MM-DD"),
      fromNum,
      durationNum,
      getCurrentChildId(),
    );
    setIsRescheduling(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setEvent(response.event ?? null);
    setIsRescheduleDialogOpen(false);
    showSuccess("Event rescheduled.");
  };

  const openWithdrawDialog = () => {
    setWithdrawReason("");
    setIsWithdrawDialogOpen(true);
  };
  const closeWithdrawDialog = () => setIsWithdrawDialogOpen(false);

  const onConfirmWithdraw = async () => {
    if (!event) return;
    if (!withdrawReason.trim()) {
      showError("Please provide a reason for withdrawal.");
      return;
    }
    setIsWithdrawing(true);
    const response = await withdrawFromEvent(
      event.id,
      withdrawReason.trim(),
      getCurrentChildId(),
    );
    setIsWithdrawing(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    if (response.event) setEvent(response.event);
    setViewerIsParticipant(false);
    setIsWithdrawDialogOpen(false);
    showSuccess("Withdrawal accepted.");
    loadDetail();
  };

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    event,
    participants,
    participantGroups,
    viewerIsCreator,
    viewerIsParticipant,
    eventHasFinished,
    eventHasStarted,
    isLoadingDetail,
    isInviteDialogOpen,
    openInviteDialog,
    closeInviteDialog,
    inviteSearchText,
    setInviteSearchText,
    inviteSearchResults,
    isSearchingInvitees,
    onInviteUser,
    invitingUserId,
    isMediaAccessDialogOpen,
    openMediaAccessDialog,
    closeMediaAccessDialog,
    onToggleParticipantMediaAccess,
    updatingMediaUserId,
    eventMedia,
    eventMediaFolders,
    isLoadingEventMedia,
    uploadTasks,
    onMediaFilesSelected,
    dismissUploadTask,
    mediaUrl,
    canCancel,
    isCancelDialogOpen,
    openCancelDialog,
    closeCancelDialog,
    onConfirmCancel,
    isCancelling,
    canReschedule,
    isRescheduleDialogOpen,
    openRescheduleDialog,
    closeRescheduleDialog,
    rescheduleDate,
    setRescheduleDate,
    rescheduleStartTime,
    setRescheduleStartTime,
    rescheduleDurationMinutes,
    setRescheduleDurationMinutes,
    onConfirmReschedule,
    isRescheduling,
    canWithdraw,
    isWithdrawDialogOpen,
    openWithdrawDialog,
    closeWithdrawDialog,
    withdrawReason,
    setWithdrawReason,
    onConfirmWithdraw,
    isWithdrawing,
    minutesToLabel,
    formatCurrency,
  };
};

export default EventDetailScreenViewModel;
